<template>
  <v-dialog
    v-model="localDialog"
    width="35%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Add Holiday" : "Update Holiday" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="HolidaySetup">
          <v-row>
            <v-col cols="12">
              <v-text-field
                label="Title"
                type="text"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="title.value.value"
                :error-messages="title.errorMessage.value"
                clearable
              ></v-text-field>

              <v-date-input
                input-format="dd/mm/yyyy"
                label="Start Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="start_date.value.value"
                :error-messages="start_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-date-input
                input-format="dd/mm/yyyy"
                label="End Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="end_date.value.value"
                :error-messages="end_date.errorMessage.value"
                autocomplete="off"
                clearable
              ></v-date-input>

              <v-select
                v-model="department_ids.value.value"
                :items="getHrmDepartmentList"
                item-title="name"
                item-value="id"
                label="Department"
                multiple
                chips
                clearable
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
              />

              <v-text-field
                label="Remarks"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
              ></v-text-field>

              <v-text-field
                label="Recurrence "
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="recurrence.value.value"
                :error-messages="recurrence.errorMessage.value"
                clearable
              ></v-text-field>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                :true-value="1"
                :false-value="0"
              ></v-checkbox>
              <input v-model="holiday_id.value.value" type="hidden" />
            </v-col>
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "HolidaySetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        holiday_id: null,
        title: "",
        start_date: "",
        end_date: "",
        remarks: "",
        department_ids: [],
        recurrence: "",
        status: 1,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
    };
  },
  computed: {
    getHrmDepartmentList() {
      return this.$store.getters.getHrmDepartmentList;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        const currentId = newData?.holiday_id ?? newData?.id ?? "";
        this.isEntryMode = !currentId;
        const normalizedDepartmentIds = this.normalizeIds(
          newData?.department_ids ?? newData?.department_id
        );
        const normalizedStatus = this.normalizeStatus(newData?.status);

        this.resetForm({
          values: {
            holiday_id: currentId || "",
            title: newData?.title || "",
            start_date: newData?.start_date || "",
            end_date: newData?.end_date || "",
            department_ids: normalizedDepartmentIds,
            remarks: newData?.remarks || "",
            recurrence: newData?.recurrence || "",
            status: normalizedStatus,
          },
        });
      },
      immediate: true,
    },
  },
  created() {
    if (this.$store.getters.getHrmDepartmentList.length === 0) {
      this.$store.dispatch("getHrmDepartmentList");
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
    normalizeIds(value) {
      if (!value) {
        return [];
      }
      if (Array.isArray(value)) {
        return value
          .map((item) => Number(item))
          .filter((item) => !Number.isNaN(item));
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed.length) {
          return [];
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => Number(item))
              .filter((item) => !Number.isNaN(item));
          }
        } catch (error) {
          return trimmed
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "")
            .map((item) => Number(item))
            .filter((item) => !Number.isNaN(item));
        }
      }
      const numeric = Number(value);
      return Number.isNaN(numeric) ? [] : [numeric];
    },
    normalizeStatus(value) {
      if (value === null || value === undefined) {
        return 1;
      }
      if (typeof value === "boolean") {
        return value ? 1 : 0;
      }
      const numeric = Number(value);
      if (Number.isNaN(numeric)) {
        return 1;
      }
      return numeric === 0 ? 0 : 1;
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        title: yup.string().required().trim().label("Title"),
        start_date: yup.string().required().label("Start Date"),
        end_date: yup.string().required().label("End Date"),
        department_ids: yup.array().label("Department"),
        remarks: yup.string().max(250).nullable().trim().label("Remarks"),
        recurrence: yup.string().max(100).nullable().trim().label("Recurrence"),
        status: yup.number().oneOf([0, 1]).label("Status"),
      }),
      // validateOnMount: false,
    });

    const holiday_id = useField("holiday_id");
    const title = useField("title");
    const start_date = useField("start_date");
    const end_date = useField("end_date");
    const department_ids = useField("department_ids");
    const remarks = useField("remarks");
    const recurrence = useField("recurrence");
    const status = useField("status");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
      const normalizeNumberArray = (list) =>
        (list || [])
          .map((item) => Number(item))
          .filter((item) => !Number.isNaN(item));

      values.start_date = formattedDate(start_date.value.value);
      values.end_date = formattedDate(end_date.value.value);
      values.department_ids = normalizeNumberArray(values.department_ids);
      values.status =
        values.status === 1 || values.status === "1" || values.status === true
          ? 1
          : 0;

      store
        .dispatch("saveUpdateHoliday", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getHolidayList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      title,
      start_date,
      end_date,
      department_ids,
      remarks,
      recurrence,
      status,
      holiday_id,
      errorMessages,
      resetForm,
      loading,
    };
  },
  mounted() {},
};
</script>
